import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Card,
    Checkbox,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextFieldProps,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ClaimData } from '../../../backendsdk';
import { ResponsiveDatePicker } from '../../../components/MuiDatePicker';
import {
    TrackedButton as Button,
    TrackedDialog as Dialog,
    TrackedIconButton as IconButton,
} from '../../../components/TrackedComponents';
import useApi from '../../../hooks/api';
import useProfile from '../../../hooks/profile';
import palette from '../../ColorPalette';

dayjs.extend(customParseFormat);

export const formatCurrency = (country: 'us' | 'il', value: number) => {
    if (country === 'il') {
        return value.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' });
    } else {
        return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    }
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1),
}));

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1),
    backgroundColor: palette.primary,
    color: palette.neutral[100],
}));

interface ClaimsModalProps {
    accidentId: number;
    accidentDate: Dayjs;
    selectedClaims: ClaimData[];
    setSelectedClaims: CallableFunction;
    setAlert: CallableFunction;
    onClose: CallableFunction;
}

const ClaimsModal: React.FC<ClaimsModalProps> = (props: ClaimsModalProps) => {
    let initialDate = props.accidentDate;
    if (props.selectedClaims.length > 0) {
        initialDate = dayjs(props.selectedClaims[0].loss_date, 'YYYY-MM-DD');
    }
    const [date, setDate] = useState<Dayjs | null>(initialDate);
    const [claims, setClaims] = useState<ClaimData[]>([]);
    const [selectedClaims, setSelectedClaims] = useState<ClaimData[]>(props.selectedClaims);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingAction, setIsLoadingAction] = useState<boolean>(false);
    const [isChanged, setIsChanged] = useState<boolean>(false);
    const { i18n, t } = useTranslation();
    const { profile } = useProfile();
    const { agencyApi } = useApi();

    useEffect(() => {
        setIsLoading(true);
        agencyApi
            .agencyV0ClaimGet({ date: date?.format('YYYY-MM-DD') })
            .then((res) => setClaims(res.data))
            .finally(() => setIsLoading(false));
    }, [date]);

    const handleChange = (updatedClaims: ClaimData[]) => {
        setIsChanged(true);
        setSelectedClaims(updatedClaims);
    };

    const handleUpdate = () => {
        setIsLoadingAction(true);
        agencyApi
            .agencyV0ClaimPatch({
                updateClaimRequest: [{ accident_id: props.accidentId, claim_ids: selectedClaims.map((c) => c.id) }],
            })
            .then(() => {
                props.setSelectedClaims(selectedClaims);
                props.onClose();
            })
            .catch(() => {
                props.setAlert({ message: 'Error associating claims to accident', type: 'error', duration: 6000 });
            })
            .finally(() => setIsLoadingAction(false));
    };

    return (
        <Dialog id="claims-modal" open={true} maxWidth="lg" fullWidth onClose={() => props.onClose()}>
            <Box
                sx={{
                    backgroundColor: 'secondary.main',
                    top: 0,
                    p: 1,
                    zIndex: 2,
                    minHeight: 0,
                    flexShrink: 0,
                }}
            >
                <Box sx={{ position: 'absolute', display: 'flex', right: 0, top: 0, mt: 0.5, mr: 0.5 }}>
                    <IconButton id="btn-close" size="small" onClick={() => props.onClose()}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ ml: 0.5 }}>{t('content.accidents.damage.select_claims')}</Typography>
                </Box>
            </Box>
            <Box sx={{ width: '100%', p: 1 }}>
                <Card sx={{ display: 'flex', flexDirection: 'column', width: '100%', p: 1, mb: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: '100%',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                        }}
                    >
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.languages[0]}>
                            <ResponsiveDatePicker
                                id="date-field"
                                label={t('content.accidents.form.date')}
                                value={date}
                                onChange={(value: Dayjs | null) => {
                                    setDate(value);
                                }}
                                maxDateTime={dayjs()}
                                slotProps={{
                                    textField: {
                                        id: 'date-field',
                                        size: 'small',
                                        sx: { direction: 'ltr', width: '175px' },
                                    } as TextFieldProps,
                                }}
                                format={profile.dateFormat}
                                sx={{ mb: 1 }}
                            />
                        </LocalizationProvider>
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                minHeight: '100px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                mt: 1,
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress />
                            ) : claims.length > 0 ? (
                                <ClaimsTable
                                    claims={claims}
                                    fullDetails
                                    selectedClaims={selectedClaims}
                                    setSelectedClaims={handleChange}
                                />
                            ) : (
                                <Typography variant="overline">
                                    {t('content.accidents.damage.no_claims_found')}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Card>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        id="btn-select-claims"
                        variant="contained"
                        color="secondary"
                        disabled={!isChanged || isLoadingAction}
                        onClick={() => {
                            handleUpdate();
                        }}
                    >
                        {t('content.accidents.damage.update')}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default ClaimsModal;

const US_ONLY_COLUMNS = ['expenses', 'salvage', 'recovery'];

const CLAIMS_TABLE_COLUMNS_PARTIAL = [
    'type',
    'date_received',
    'paid',
    'reserve',
    'expenses',
    'salvage',
    'recovery',
    'total',
    'status',
];

const CLAIMS_TABLE_COLUMNS_FULL = [
    'insurer',
    'claim_number',
    'sub_claim_number',
    'type',
    'loss_date',
    'date_received',
    'paid',
    'reserve',
    'expenses',
    'salvage',
    'recovery',
    'total',
    'status',
];

const DATE_COLUMNS = ['loss_date', 'date_received'];

const CURRENCY_COLUMNS = ['paid', 'reserve', 'expenses', 'salvage', 'recovery', 'total'];

interface ClaimsTableProps {
    claims: ClaimData[];
    fullDetails?: boolean;
    selectedClaims?: ClaimData[];
    setSelectedClaims?: CallableFunction;
}

export const ClaimsTable: React.FC<ClaimsTableProps> = (props: ClaimsTableProps) => {
    const { profile } = useProfile();
    const { t } = useTranslation();
    let columns = props.fullDetails ? CLAIMS_TABLE_COLUMNS_FULL : CLAIMS_TABLE_COLUMNS_PARTIAL;
    if (profile.customer.settings.country === 'il') {
        columns = columns.filter((column) => !US_ONLY_COLUMNS.includes(column));
    }

    return (
        <TableContainer sx={{ direction: 'ltr' }}>
            <Table>
                <TableHead>
                    {!!props.setSelectedClaims ? (
                        <StyledHeaderCell>
                            <Typography variant="overline" sx={{ lineHeight: 1 }}>
                                {t(`content.accidents.damage.claims_table.select`)}
                            </Typography>
                        </StyledHeaderCell>
                    ) : null}
                    {columns.map((column, index) => (
                        <StyledHeaderCell key={index}>
                            <Typography variant="overline" sx={{ lineHeight: 1 }}>
                                {t(`content.accidents.damage.claims_table.${column}`)}
                            </Typography>
                        </StyledHeaderCell>
                    ))}
                </TableHead>
                <TableBody>
                    {props.claims.map((claim, rowIndex) => (
                        <TableRow
                            key={rowIndex}
                            selected={(props.selectedClaims || []).map((c) => c.id).includes(claim.id)}
                        >
                            {!!props.setSelectedClaims ? (
                                <StyledTableCell>
                                    <Checkbox
                                        id={`claim-${claim.id}-select`}
                                        checked={(props.selectedClaims || [])
                                            .map((claim) => claim.id)
                                            .includes(claim.id)}
                                        onChange={() => {
                                            if (props.setSelectedClaims) {
                                                props.setSelectedClaims((prev: ClaimData[]) => {
                                                    const claim_number = claim.claim_number;
                                                    const insurer = claim.insurer;
                                                    if ((prev || []).map((claim) => claim.id).includes(claim.id)) {
                                                        return prev.filter(
                                                            (c) =>
                                                                c.claim_number !== claim_number ||
                                                                c.insurer !== insurer,
                                                        );
                                                    }
                                                    const subClaims = props.claims.filter(
                                                        (c) => c.claim_number === claim_number && c.insurer === insurer,
                                                    );
                                                    return [...prev, ...subClaims];
                                                });
                                            }
                                        }}
                                    />
                                </StyledTableCell>
                            ) : null}
                            {columns.map((column, colIndex) => (
                                <StyledTableCell
                                    key={`row-${rowIndex}-col-${colIndex}`}
                                    id={`row-${rowIndex}-col-${colIndex}`}
                                >
                                    <Typography variant="body2">
                                        {DATE_COLUMNS.includes(column)
                                            ? dayjs(claim[column as keyof ClaimData], 'YYYY-MM-DD').format(
                                                  profile.dateFormat,
                                              )
                                            : CURRENCY_COLUMNS.includes(column)
                                            ? formatCurrency(
                                                  profile.customer.settings.country as 'us' | 'il',
                                                  claim[column as keyof ClaimData] as number,
                                              )
                                            : claim[column as keyof ClaimData]}
                                    </Typography>
                                </StyledTableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
